import React from 'react';
import {
  Box,
  Heading,
  Table,
  TableHeader,
  TableHeaderRow,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TextInput,
  IconButton,
  Link,
  Text,
  Dropdown,
  SelectInput,
  DropdownOverlay,
  ActionList,
  ActionListItem,
} from '@razorpay/blade/components';
import { TrashIcon, PlusIcon } from '@razorpay/blade/components';
import { useEmployeeCustomSalaryStructure } from './hooks/useEmployeeCustomSalaryStructure';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { getTaxableLabel } from './utils';
import { useSelector } from 'react-redux';
import { TaxableValue, taxableValues } from './constants';

interface SalaryStructureTableProps {
  employeeCustomSalaryStructureHandlers: ReturnType<typeof useEmployeeCustomSalaryStructure>;
  isFormBeingSubmitted: boolean;
}

export const CustomSalaryStructureForm: React.FC<SalaryStructureTableProps> = ({
  employeeCustomSalaryStructureHandlers,
  isFormBeingSubmitted,
}) => {
  const { components, addNewComponent, removeComponent, updateComponent } =
    employeeCustomSalaryStructureHandlers;

  const referenceLinks = useSelector(loggedInUserSelectors.referenceLinks);

  const handleAmountChange = (componentId: string, value: string) => {
    if (!value || /^\d+$/.test(value)) {
      updateComponent({
        id: componentId,
        component: { amount: value },
      });
    }
  };

  const handleTaxableChange = (componentId: string, value: string) => {
    updateComponent({
      id: componentId,
      component: { taxable: value as TaxableValue },
    });
  };

  const handleNameChange = (componentId: string, value: string) => {
    updateComponent({
      id: componentId,
      component: { name: value },
    });
  };

  // Transform components for table display
  const tableData = {
    nodes: components,
  };

  return (
    <Box>
      {/* Header with total amount */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        marginBottom="spacing.4"
        marginTop="spacing.3">
        <Box>
          <Box display="flex" gap="spacing.1">
            <Heading size="medium" color="surface.text.gray.normal">
              Earnings
            </Heading>
            <Heading size="medium" weight="regular" color="surface.text.gray.muted">
              (Gross Pay)
            </Heading>
          </Box>
          <Text size="small" color="surface.text.gray.muted">
            The salary earned by the employee before the deductions
          </Text>
        </Box>
      </Box>

      {/* Salary Structure Table */}
      <Table data={tableData} gridTemplateColumns="1fr 200px 100px 50px">
        {(tableData) => (
          <>
            <TableHeader>
              <TableHeaderRow>
                <TableHeaderCell>Component</TableHeaderCell>
                <TableHeaderCell textAlign="right">Monthly Amount</TableHeaderCell>
                <TableHeaderCell>Taxable</TableHeaderCell>
                <TableHeaderCell> </TableHeaderCell>
              </TableHeaderRow>
            </TableHeader>

            <TableBody>
              {tableData.map((component, index) => (
                <TableRow key={component.id} item={component}>
                  <TableCell>
                    {component.isNameEditable ? (
                      <TextInput
                        placeholder="Enter component name"
                        value={component.name}
                        onChange={({ value }) => handleNameChange(component.id, value || '')}
                        isDisabled={isFormBeingSubmitted}
                        size="medium"
                        accessibilityLabel="Component name"
                      />
                    ) : (
                      <Text weight="medium">{component.name}</Text>
                    )}
                  </TableCell>

                  <TableCell>
                    <TextInput
                      placeholder="Enter Amount"
                      value={component.amount}
                      onChange={({ value }) => handleAmountChange(component.id, value || '')}
                      isDisabled={isFormBeingSubmitted}
                      type="number"
                      textAlign="right"
                      size="medium"
                      accessibilityLabel={`Amount for ${component.name}`}
                    />
                  </TableCell>

                  <TableCell>
                    <Box minWidth="80px">
                      {component.isTaxableEditable ? (
                        <Dropdown selectionType="single">
                          <SelectInput
                            label=""
                            value={component.taxable}
                            onChange={({ values }) =>
                              handleTaxableChange(component.id, values[0] || 'no')
                            }
                          />
                          <DropdownOverlay>
                            <ActionList>
                              {taxableValues.map((option) => (
                                <ActionListItem
                                  key={option.value}
                                  title={option.label}
                                  value={option.value}
                                  isSelected={component.taxable === option.value}
                                />
                              ))}
                            </ActionList>
                          </DropdownOverlay>
                        </Dropdown>
                      ) : (
                        getTaxableLabel(component, referenceLinks)
                      )}
                    </Box>
                  </TableCell>

                  <TableCell textAlign="center">
                    {component.isRemovable && (
                      <IconButton
                        icon={TrashIcon}
                        accessibilityLabel={`Remove ${component.name}`}
                        onClick={() => removeComponent({ id: component.id })}
                        isDisabled={isFormBeingSubmitted}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </>
        )}
      </Table>

      {/* Add Component Link */}
      <Box marginTop="spacing.3">
        <Link
          variant="button"
          icon={PlusIcon}
          iconPosition="left"
          onClick={addNewComponent}
          isDisabled={isFormBeingSubmitted}
          size="small">
          Add Component
        </Link>
      </Box>
    </Box>
  );
};
